import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Shield, Eye, EyeOff, MessageSquare, Users, Search,
  Trash2, AlertTriangle, Lock, Unlock, UserX, Volume2,
  VolumeX, ArrowLeft, RefreshCw, Clock, CheckCircle, XCircle,
  Download, FileText, Settings, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrivacySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletionType, setDeletionType] = useState('soft');

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, blockedRes, mutedRes, deletionRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/privacy/settings`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/privacy/blocked`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/privacy/muted`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/privacy/deletion-status`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/privacy/activity-log?limit=50`, { headers: getHeaders() })
      ]);

      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (blockedRes.ok) {
        const data = await blockedRes.json();
        setBlockedUsers(data.blocked_users || []);
      }
      if (mutedRes.ok) {
        const data = await mutedRes.json();
        setMutedUsers(data.muted_users || []);
      }
      if (deletionRes.ok) setDeletionStatus(await deletionRes.json());
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivityLog(data.activity_log || []);
      }
    } catch (error) {
      console.error('Error fetching privacy data:', error);
      toast.error('Failed to load privacy settings');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/privacy/settings`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ [key]: value })
      });

      if (res.ok) {
        setSettings(prev => ({ ...prev, [key]: value }));
        toast.success('Setting updated');
      } else {
        toast.error('Failed to update setting');
      }
    } catch (error) {
      toast.error('Error updating setting');
    } finally {
      setSaving(false);
    }
  };

  const handleUnblock = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/block/${userId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.ok) {
        setBlockedUsers(prev => prev.filter(b => b.blocked_user_id !== userId));
        toast.success('User unblocked');
      }
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleUnmute = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/mute/${userId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.ok) {
        setMutedUsers(prev => prev.filter(m => m.muted_user_id !== userId));
        toast.success('User unmuted');
      }
    } catch (error) {
      toast.error('Failed to unmute user');
    }
  };

  const requestDeletion = async () => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/delete-account`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          deletion_type: deletionType,
          confirm: true,
          reason: 'User requested deletion'
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Deletion request submitted');
        setShowDeleteConfirm(false);
        setDeletionStatus({ has_pending_deletion: true, pending_requests: [data] });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit deletion request');
      }
    } catch (error) {
      toast.error('Error submitting deletion request');
    }
  };

  const cancelDeletion = async (deletionId) => {
    try {
      const res = await fetch(`${API_URL}/api/privacy/delete-account/cancel`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ deletion_id: deletionId })
      });

      if (res.ok) {
        toast.success('Deletion cancelled');
        setDeletionStatus({ has_pending_deletion: false, pending_requests: [] });
      }
    } catch (error) {
      toast.error('Failed to cancel deletion');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'settings', label: 'Privacy', icon: Shield },
    { id: 'blocked', label: `Blocked (${blockedUsers.length})`, icon: UserX },
    { id: 'muted', label: `Muted (${mutedUsers.length})`, icon: VolumeX },
    { id: 'data', label: 'Data', icon: FileText },
    { id: 'activity', label: 'Activity', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/settings')}
            className="text-gray-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="h-6 w-6 text-purple-400" />
              Privacy & Data
            </h1>
            <p className="text-gray-400 text-sm">Control your privacy settings and manage your data</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-slate-800/50 p-1 rounded-xl overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div className="space-y-4">
            {/* Post Visibility */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="h-5 w-5 text-purple-400" />
                  Post Visibility
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Control who can see your posts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {['public', 'followers_only', 'private'].map(option => (
                  <button
                    key={option}
                    onClick={() => updateSetting('post_visibility', option)}
                    className={`w-full p-4 rounded-lg border transition text-left ${
                      settings.post_visibility === option
                        ? 'border-purple-500 bg-purple-900/30'
                        : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                    }`}
                    data-testid={`visibility-${option}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium capitalize">{option.replace('_', ' ')}</p>
                        <p className="text-gray-400 text-sm">
                          {option === 'public' && 'Anyone can see your posts'}
                          {option === 'followers_only' && 'Only your followers can see'}
                          {option === 'private' && 'Only you can see your posts'}
                        </p>
                      </div>
                      {settings.post_visibility === option && (
                        <CheckCircle className="h-5 w-5 text-purple-400" />
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* DM Permissions */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-400" />
                  Direct Messages
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Control who can send you DMs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {['everyone', 'followers', 'mutual_follows', 'nobody'].map(option => (
                  <button
                    key={option}
                    onClick={() => updateSetting('dm_permission', option)}
                    className={`w-full p-4 rounded-lg border transition text-left ${
                      settings.dm_permission === option
                        ? 'border-blue-500 bg-blue-900/30'
                        : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                    }`}
                    data-testid={`dm-${option}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium capitalize">{option.replace('_', ' ')}</p>
                        <p className="text-gray-400 text-sm">
                          {option === 'everyone' && 'Anyone can DM you'}
                          {option === 'followers' && 'Only your followers can DM'}
                          {option === 'mutual_follows' && 'Only mutual follows can DM'}
                          {option === 'nobody' && 'DMs are disabled'}
                        </p>
                      </div>
                      {settings.dm_permission === option && (
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Toggle Settings */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="h-5 w-5 text-green-400" />
                  Additional Privacy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'show_online_status', label: 'Show online status', desc: 'Let others see when you\'re online' },
                  { key: 'show_last_seen', label: 'Show last seen', desc: 'Let others see when you were last active' },
                  { key: 'show_read_receipts', label: 'Read receipts', desc: 'Let others know when you\'ve read their messages' },
                  { key: 'allow_tagging', label: 'Allow tagging', desc: 'Let others tag you in posts' },
                  { key: 'allow_mentions', label: 'Allow mentions', desc: 'Let others mention you in comments' },
                  { key: 'show_in_search', label: 'Show in search', desc: 'Appear in search results' },
                  { key: 'show_in_suggestions', label: 'Show in suggestions', desc: 'Appear in user suggestions' },
                  { key: 'hide_likes_count', label: 'Hide likes count', desc: 'Hide the number of likes on your posts' },
                  { key: 'hide_followers_count', label: 'Hide followers count', desc: 'Hide your follower count' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{item.label}</p>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                    <Switch
                      checked={settings[item.key] || false}
                      onCheckedChange={(checked) => updateSetting(item.key, checked)}
                      data-testid={`toggle-${item.key}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Blocked Users Tab */}
        {activeTab === 'blocked' && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Blocked Users</CardTitle>
              <CardDescription className="text-gray-400">
                Users you've blocked cannot see your content or contact you
              </CardDescription>
            </CardHeader>
            <CardContent>
              {blockedUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <UserX className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>You haven't blocked anyone</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {blockedUsers.map((block, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {block.user?.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{block.user?.name || 'Unknown'}</p>
                          <p className="text-gray-400 text-sm">@{block.user?.username || 'unknown'}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnblock(block.blocked_user_id)}
                        className="border-red-600 text-red-400 hover:bg-red-900/30"
                        data-testid={`unblock-${block.blocked_user_id}`}
                      >
                        Unblock
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Muted Users Tab */}
        {activeTab === 'muted' && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Muted Users</CardTitle>
              <CardDescription className="text-gray-400">
                Muted users' content is hidden from your feed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mutedUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <VolumeX className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>You haven't muted anyone</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {mutedUsers.map((mute, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold">
                          {mute.user?.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{mute.user?.name || 'Unknown'}</p>
                          <p className="text-gray-400 text-sm">@{mute.user?.username || 'unknown'}</p>
                          {mute.expires_at && (
                            <p className="text-amber-400 text-xs">Expires: {new Date(mute.expires_at).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnmute(mute.muted_user_id)}
                        className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                        data-testid={`unmute-${mute.muted_user_id}`}
                      >
                        Unmute
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Tab */}
        {activeTab === 'data' && (
          <div className="space-y-4">
            {/* Download Data */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Download className="h-5 w-5 text-blue-400" />
                  Download Your Data
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Get a copy of all your data (GDPR compliant)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => window.open(`${API_URL}/api/compliance/my-data`, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="download-data-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download My Data
                </Button>
              </CardContent>
            </Card>

            {/* Delete Account */}
            <Card className="bg-slate-800/50 border-red-900/50">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Delete Account
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Permanently delete your account and data
                </CardDescription>
              </CardHeader>
              <CardContent>
                {deletionStatus?.has_pending_deletion ? (
                  <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                      <span className="text-red-300 font-medium">Deletion Scheduled</span>
                    </div>
                    <p className="text-gray-300 text-sm mb-3">
                      Your account is scheduled for deletion on{' '}
                      {new Date(deletionStatus.pending_requests[0]?.scheduled_at).toLocaleDateString()}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => cancelDeletion(deletionStatus.pending_requests[0]?.deletion_id)}
                      className="border-green-600 text-green-400 hover:bg-green-900/30"
                      data-testid="cancel-deletion-btn"
                    >
                      Cancel Deletion
                    </Button>
                  </div>
                ) : showDeleteConfirm ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
                      <p className="text-red-300 font-medium mb-2">Choose deletion type:</p>
                      <div className="space-y-2">
                        <button
                          onClick={() => setDeletionType('soft')}
                          className={`w-full p-3 rounded-lg border text-left ${
                            deletionType === 'soft'
                              ? 'border-red-500 bg-red-900/50'
                              : 'border-slate-600 bg-slate-700/30'
                          }`}
                        >
                          <p className="text-white font-medium">Soft Delete</p>
                          <p className="text-gray-400 text-sm">Anonymizes your data but keeps post structure</p>
                        </button>
                        <button
                          onClick={() => setDeletionType('hard')}
                          className={`w-full p-3 rounded-lg border text-left ${
                            deletionType === 'hard'
                              ? 'border-red-500 bg-red-900/50'
                              : 'border-slate-600 bg-slate-700/30'
                          }`}
                        >
                          <p className="text-white font-medium">Hard Delete</p>
                          <p className="text-gray-400 text-sm">Permanently removes ALL your data</p>
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="border-slate-600"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={requestDeletion}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="confirm-delete-btn"
                      >
                        Confirm Delete (30-day grace period)
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="border-red-600 text-red-400 hover:bg-red-900/30"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete My Account
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activity' && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-400" />
                Privacy Activity Log
              </CardTitle>
              <CardDescription className="text-gray-400">
                Recent privacy-related actions on your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activityLog.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No activity yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activityLog.map((log, idx) => (
                    <div key={idx} className="p-3 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium">{log.action?.replace(/_/g, ' ')}</p>
                        <span className="text-gray-400 text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {log.details && (
                        <p className="text-gray-400 text-sm mt-1">
                          {JSON.stringify(log.details)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
